import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  ArrowRight, ArrowLeft, Building2, User, Briefcase,
  PoundSterling, CheckCircle2, AlertCircle, Users,
  FileText, Target, Home, Sparkles, Search, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const WIZARD_STEPS = [
  { id: 'sector', title: 'Sector', icon: Building2 },
  { id: 'project_type', title: 'Project Type', icon: Briefcase },
  { id: 'client', title: 'Client Details', icon: User },
  { id: 'client_type', title: 'Client Type', icon: Users },
  { id: 'project_details', title: 'Project Details', icon: FileText },
  { id: 'clarity', title: 'Project Clarity', icon: Target },
  { id: 'value', title: 'Value & Scale', icon: PoundSterling },
  { id: 'review', title: 'Review', icon: CheckCircle2 },
];

const RESIDENTIAL_STEPS = [
  { id: 'res_scale', title: 'Scale Check', icon: PoundSterling },
  { id: 'res_criteria', title: 'Selection Criteria', icon: Sparkles },
];

const SECTORS = [
  { id: 'commercial_fitout', label: 'Commercial Fit-out', description: 'CAT A/B office fit-outs', primary: true },
  { id: 'residential', label: 'Residential', description: 'Selected projects only', primary: false },
  { id: 'retail', label: 'Retail', description: 'Shops & showrooms', primary: false },
  { id: 'healthcare', label: 'Healthcare', description: 'Clinics & medical', primary: false },
  { id: 'education', label: 'Education', description: 'Schools & universities', primary: false },
  { id: 'other', label: 'Other', description: 'Specify your sector', primary: false },
];

const PROJECT_TYPES = [
  { id: 'cat_a', label: 'CAT A', description: 'Shell & core base building services' },
  { id: 'cat_b', label: 'CAT B', description: 'Tenant fit-out works' },
  { id: 'refurbishment', label: 'Refurbishment', description: 'Existing building upgrade' },
  { id: 'new_build', label: 'New Build', description: 'Ground-up construction' },
];

const CLIENT_TYPES = [
  { id: 'contractor', label: 'Main Contractor' },
  { id: 'project_manager', label: 'Project Manager' },
  { id: 'quantity_surveyor', label: 'Quantity Surveyor' },
  { id: 'developer', label: 'Developer' },
  { id: 'landlord', label: 'Landlord' },
  { id: 'architect', label: 'Architect' },
];

const DELIVERY_AUTHORITY = [
  { id: 'confirmed', label: 'Confirmed', description: 'Has authority to proceed' },
  { id: 'likely', label: 'Likely', description: 'Expected to have authority' },
  { id: 'unknown', label: 'Unknown', description: 'Not yet confirmed' },
];

const PROJECT_STAGES = [
  { id: 'feasibility', label: 'Feasibility' },
  { id: 'concept', label: 'Concept Design' },
  { id: 'developed_design', label: 'Developed Design' },
  { id: 'technical_design', label: 'Technical Design' },
  { id: 'construction', label: 'Construction' },
];

const MEP_DISCIPLINES = [
  { id: 'mechanical', label: 'Mechanical' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'fire', label: 'Fire Protection' },
];

export const EnquiryWizard = () => {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isResidential, setIsResidential] = useState(false);
  const [residentialStep, setResidentialStep] = useState(0);
  const [showResidentialWizard, setShowResidentialWizard] = useState(false);
  const [showOtherSectorInput, setShowOtherSectorInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Pipeline contacts for linking
  const [contacts, setContacts] = useState([]);
  const [useExistingContact, setUseExistingContact] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [resThresholds, setResThresholds] = useState({ scale_threshold: 500000, fee_threshold: 50000, min_criteria_pass: 3 });
  
  // Fetch contacts and residential thresholds on mount
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await authFetch('/pipeline/contacts');
        if (res.ok) {
          const data = await res.json();
          setContacts(data.contacts || []);
        }
      } catch (err) {
        console.error('Failed to fetch contacts:', err);
      }
    };
    const fetchThresholds = async () => {
      try {
        const res = await authFetch('/admin/residential-thresholds');
        if (res.ok) {
          const data = await res.json();
          setResThresholds(data.thresholds || resThresholds);
        }
      } catch { /* use defaults */ }
    };
    fetchContacts();
    fetchThresholds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch]);
  
  // Filter contacts based on search
  const filteredContacts = contacts.filter(c => 
    c.contact_name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(contactSearch.toLowerCase())
  ).sort((a, b) => (a.contact_name || '').localeCompare(b.contact_name || ''));
  
  // Handle selecting an existing contact
  const handleSelectContact = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setSelectedContactId(contactId);
      setFormData(prev => ({
        ...prev,
        contact_id: contactId,
        client_name: contact.contact_name || '',
        client_email: contact.email || '',
        client_phone: contact.phone || '',
        client_company: contact.company_name || ''
      }));
    }
  };
  
  const [formData, setFormData] = useState({
    sector: '',
    sector_custom_name: '',  // Required when sector == "other"
    project_type: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company: '',
    client_type: '',
    delivery_authority: '',
    project_name: '',
    location: '',
    description: '',
    project_stage: '',
    has_programme_summary: false,
    has_scope_summary: false,
    decision_maker_name: '',
    estimated_value: '',
    sqft: '',
    timeline_start: '',
    timeline_end: '',
    mep_disciplines: [],
    source: '',
    contact_id: null,
    // Residential criteria
    residential_criteria: {
      meets_scale_threshold: false,
      meets_fee_threshold: false,
      client_quality_score: 5,
      has_strategic_value: false,
      delivery_risk_manageable: false,
      alignment_with_strengths: false,
    }
  });

  const steps = isResidential && showResidentialWizard 
    ? RESIDENTIAL_STEPS 
    : WIZARD_STEPS;
  
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  
  // Active step calculated for potential future use (e.g., step title display)
  const _activeStep = isResidential && showResidentialWizard 
    ? RESIDENTIAL_STEPS[residentialStep] 
    : WIZARD_STEPS[currentStep];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateResidential = (field, value) => {
    setFormData(prev => ({
      ...prev,
      residential_criteria: { ...prev.residential_criteria, [field]: value }
    }));
  };

  const handleNext = () => {
    if (isResidential && showResidentialWizard) {
      if (residentialStep < RESIDENTIAL_STEPS.length - 1) {
        setResidentialStep(prev => prev + 1);
      } else {
        // Return to main wizard
        setShowResidentialWizard(false);
        setCurrentStep(1); // Continue after sector
      }
    } else if (showOtherSectorInput) {
      // "Other" sector custom name has been provided, proceed to main wizard
      setShowOtherSectorInput(false);
      setCurrentStep(1); // Continue after sector
    } else {
      // Check if sector is residential and we haven't done the sub-wizard
      if (currentStep === 0 && formData.sector === 'residential' && !showResidentialWizard) {
        setIsResidential(true);
        setShowResidentialWizard(true);
        setResidentialStep(0);
      } else if (currentStep === 0 && formData.sector === 'other' && !showOtherSectorInput) {
        // Show "Other" sector input
        setShowOtherSectorInput(true);
      } else if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (isResidential && showResidentialWizard) {
      if (residentialStep > 0) {
        setResidentialStep(prev => prev - 1);
      } else {
        setShowResidentialWizard(false);
        setCurrentStep(0);
      }
    } else if (showOtherSectorInput) {
      setShowOtherSectorInput(false);
      setCurrentStep(0);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        sqft: formData.sqft ? parseFloat(formData.sqft) : null,
        // Only include sector_custom_name if sector is "other"
        sector_custom_name: formData.sector === 'other' ? formData.sector_custom_name : null,
      };
      
      const res = await authFetch('/enquiries', {
        method: 'POST',
        body: payload
      });
      
      if (res.ok) {
        const enquiry = await res.json();
        toast.success('Enquiry created successfully');
        navigate(`/enquiry/${enquiry.id}/decision`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create enquiry');
      }
    } catch (err) {
      toast.error('Failed to create enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    // "Other" sector custom name input validation
    if (showOtherSectorInput) {
      return formData.sector_custom_name && formData.sector_custom_name.trim().length >= 2;
    }
    
    if (isResidential && showResidentialWizard) {
      if (residentialStep === 0) {
        return formData.residential_criteria.meets_scale_threshold && 
               formData.residential_criteria.meets_fee_threshold;
      }
      return true;
    }
    
    switch (WIZARD_STEPS[currentStep]?.id) {
      case 'sector': return formData.sector;
      case 'project_type': return formData.project_type;
      case 'client': return formData.client_name && formData.client_company;
      case 'client_type': return formData.client_type;
      case 'project_details': return formData.project_name;
      case 'clarity': return formData.project_stage;
      case 'value': return formData.estimated_value;
      case 'review': return true;
      default: return true;
    }
  };

  const renderStepContent = () => {
    // Residential Sub-Wizard
    if (isResidential && showResidentialWizard) {
      if (residentialStep === 0) {
        return (
          <div className="space-y-8 animate-fade-in" data-testid="res-scale-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Residential Scale Check
              </h2>
              <p className="text-slate-500 text-lg">
                Residential projects must meet minimum thresholds to qualify
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Selected Projects Only</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Residential is our secondary market. Projects must meet strict criteria.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <Checkbox
                  id="scale_threshold"
                  checked={formData.residential_criteria.meets_scale_threshold}
                  onCheckedChange={(checked) => updateResidential('meets_scale_threshold', checked)}
                  data-testid="res-scale-checkbox"
                />
                <div className="flex-1">
                  <Label htmlFor="scale_threshold" className="font-medium text-slate-700">
                    Construction value ≥ £{(resThresholds.scale_threshold || 500000).toLocaleString('en-GB')}
                  </Label>
                  <p className="text-sm text-slate-500">Minimum project scale requirement</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <Checkbox
                  id="fee_threshold"
                  checked={formData.residential_criteria.meets_fee_threshold}
                  onCheckedChange={(checked) => updateResidential('meets_fee_threshold', checked)}
                  data-testid="res-fee-checkbox"
                />
                <div className="flex-1">
                  <Label htmlFor="fee_threshold" className="font-medium text-slate-700">
                    Expected fee ≥ £{(resThresholds.fee_threshold || 50000).toLocaleString('en-GB')}
                  </Label>
                  <p className="text-sm text-slate-500">Minimum fee value requirement</p>
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      // Residential criteria step
      return (
        <div className="space-y-8 animate-fade-in" data-testid="res-criteria-step">
          <div className="space-y-2">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
              Selection Criteria
            </h2>
            <p className="text-slate-500 text-lg">
              Must meet at least 3 of 5 criteria to proceed
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <Checkbox
                checked={formData.residential_criteria.client_quality_score >= 7}
                onCheckedChange={(checked) => updateResidential('client_quality_score', checked ? 8 : 5)}
                data-testid="res-client-quality"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-700">Client Quality</p>
                <p className="text-sm text-slate-500">Reputable client with good track record</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <Checkbox
                checked={formData.residential_criteria.has_strategic_value}
                onCheckedChange={(checked) => updateResidential('has_strategic_value', checked)}
                data-testid="res-strategic-value"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-700">Strategic Value</p>
                <p className="text-sm text-slate-500">Opens new opportunities or relationships</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <Checkbox
                checked={formData.residential_criteria.delivery_risk_manageable}
                onCheckedChange={(checked) => updateResidential('delivery_risk_manageable', checked)}
                data-testid="res-delivery-risk"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-700">Delivery Risk Manageable</p>
                <p className="text-sm text-slate-500">Realistic timeline and clear scope</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <Checkbox
                checked={formData.residential_criteria.alignment_with_strengths}
                onCheckedChange={(checked) => updateResidential('alignment_with_strengths', checked)}
                data-testid="res-alignment"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-700">Alignment with Strengths</p>
                <p className="text-sm text-slate-500">Matches our transferable expertise</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <div className="flex-1">
                <p className="font-medium text-emerald-700">Scale & Fee Threshold</p>
                <p className="text-sm text-emerald-600">Already confirmed ✓</p>
              </div>
            </div>
          </div>
          
          {/* Criteria count */}
          <div className="bg-slate-100 rounded-xl p-4">
            <p className="text-sm font-medium text-slate-600">
              Criteria met: {
                [
                  formData.residential_criteria.client_quality_score >= 7,
                  formData.residential_criteria.has_strategic_value,
                  formData.residential_criteria.delivery_risk_manageable,
                  formData.residential_criteria.alignment_with_strengths,
                  true // scale/fee already met
                ].filter(Boolean).length
              } / 5 (need 3)
            </p>
          </div>
        </div>
      );
    }

    // "Other" Sector Custom Name Input
    if (showOtherSectorInput) {
      return (
        <div className="space-y-8 animate-fade-in" data-testid="other-sector-step">
          <div className="space-y-2">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
              Specify Your Sector
            </h2>
            <p className="text-slate-500 text-lg">
              Please provide the name of the sector for this project
            </p>
          </div>
          
          <div className="bg-sky-50 border border-sky-200 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-sky-600 mt-0.5" />
              <div>
                <p className="font-medium text-sky-800">Custom Sector</p>
                <p className="text-sm text-sky-700 mt-1">
                  This sector will be recorded and used for analytics and reporting.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="sector_custom_name" className="text-slate-700 font-medium">
              Sector Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="sector_custom_name"
              value={formData.sector_custom_name}
              onChange={(e) => updateField('sector_custom_name', e.target.value)}
              placeholder="e.g., Data Centres, Industrial, Hospitality"
              className="h-12 text-lg"
              data-testid="sector-custom-name-input"
            />
            {formData.sector_custom_name && formData.sector_custom_name.trim().length < 2 && (
              <p className="text-sm text-red-500">Sector name must be at least 2 characters</p>
            )}
          </div>
        </div>
      );
    }

    // Main Wizard Steps
    switch (WIZARD_STEPS[currentStep]?.id) {
      case 'sector':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="sector-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                What sector is this project?
              </h2>
              <p className="text-slate-500 text-lg">
                Our primary focus is commercial office fit-outs
              </p>
            </div>
            
            <RadioGroup
              value={formData.sector}
              onValueChange={(value) => {
                updateField('sector', value);
                setIsResidential(value === 'residential');
              }}
              className="space-y-3"
            >
              {SECTORS.map((sector) => (
                <div
                  key={sector.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    formData.sector === sector.id 
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => {
                    updateField('sector', sector.id);
                    setIsResidential(sector.id === 'residential');
                  }}
                  data-testid={`sector-${sector.id}`}
                >
                  <RadioGroupItem value={sector.id} id={sector.id} className="border-[#1e3a5f]" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={sector.id} className="font-medium text-slate-700 cursor-pointer">
                        {sector.label}
                      </Label>
                      {sector.primary && (
                        <span className="chip chip-navy">Primary</span>
                      )}
                      {!sector.primary && sector.id === 'residential' && (
                        <span className="chip chip-warning">Selected Only</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{sector.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
        
      case 'project_type':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="project-type-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                What type of project?
              </h2>
              <p className="text-slate-500 text-lg">
                Select the project category
              </p>
            </div>
            
            <RadioGroup
              value={formData.project_type}
              onValueChange={(value) => updateField('project_type', value)}
              className="space-y-3"
            >
              {PROJECT_TYPES.map((type) => (
                <div
                  key={type.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    formData.project_type === type.id 
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => updateField('project_type', type.id)}
                  data-testid={`project-type-${type.id}`}
                >
                  <RadioGroupItem value={type.id} id={type.id} className="border-[#1e3a5f]" />
                  <div className="flex-1">
                    <Label htmlFor={type.id} className="font-medium text-slate-700 cursor-pointer">
                      {type.label}
                    </Label>
                    <p className="text-sm text-slate-500">{type.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
        
      case 'client':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="client-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Client Details
              </h2>
              <p className="text-slate-500 text-lg">
                Who is the main contact for this enquiry?
              </p>
            </div>
            
            {/* Toggle between existing contact and new contact */}
            <div className="flex gap-3 p-1 bg-slate-100 rounded-xl">
              <button
                type="button"
                onClick={() => {
                  setUseExistingContact(false);
                  setSelectedContactId('');
                  setFormData(prev => ({ ...prev, contact_id: null }));
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg transition-all ${
                  !useExistingContact 
                    ? 'bg-white text-[#1e3a5f] shadow-sm font-medium' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="new-contact-tab"
              >
                <UserPlus className="w-4 h-4" />
                New Contact
              </button>
              <button
                type="button"
                onClick={() => setUseExistingContact(true)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg transition-all ${
                  useExistingContact 
                    ? 'bg-white text-[#1e3a5f] shadow-sm font-medium' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="existing-contact-tab"
              >
                <Search className="w-4 h-4" />
                Existing Contact ({contacts.length})
              </button>
            </div>
            
            {useExistingContact ? (
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="Search contacts by name, company, or email..."
                    className="h-12 pl-10 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="contact-search-input"
                  />
                </div>
                
                <div className="max-h-[50dvh] sm:max-h-80 overflow-y-auto space-y-2 border border-slate-200 rounded-xl p-2" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {filteredContacts.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">No contacts found</p>
                  ) : (
                    filteredContacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => handleSelectContact(contact.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedContactId === contact.id
                            ? 'bg-[#1e3a5f] text-white'
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                        data-testid={`contact-option-${contact.id}`}
                      >
                        <p className={`font-medium ${selectedContactId === contact.id ? 'text-white' : 'text-slate-800'}`}>
                          {contact.contact_name}
                        </p>
                        <p className={`text-sm ${selectedContactId === contact.id ? 'text-white/70' : 'text-slate-500'}`}>
                          {contact.company_name} {contact.email && `• ${contact.email}`}
                        </p>
                      </div>
                    ))
                  )}
                </div>
                
                {selectedContactId && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <p className="text-emerald-800 font-medium flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Contact selected - details will be pre-filled
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Contact Name *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => updateField('client_name', e.target.value)}
                      placeholder="John Smith"
                      className="h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Company *</Label>
                    <Input
                      value={formData.client_company}
                      onChange={(e) => updateField('client_company', e.target.value)}
                      placeholder="Acme Construction"
                      className="h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="client-company-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Email</Label>
                    <Input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => updateField('client_email', e.target.value)}
                      placeholder="john@company.com"
                      className="h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Phone</Label>
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => updateField('client_phone', e.target.value)}
                      placeholder="+44 20 1234 5678"
                      className="h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="client-phone-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        
      case 'client_type':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="client-type-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                What type of client?
              </h2>
              <p className="text-slate-500 text-lg">
                Must be contractor, PM, QS, developer, landlord, or architect with delivery authority
              </p>
            </div>
            
            <RadioGroup
              value={formData.client_type}
              onValueChange={(value) => updateField('client_type', value)}
              className="grid grid-cols-2 gap-3"
            >
              {CLIENT_TYPES.map((type) => (
                <div
                  key={type.id}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer select-none touch-manipulation ${
                    formData.client_type === type.id 
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                      : 'border-slate-200 hover:border-slate-300 active:bg-slate-50'
                  }`}
                  onClick={() => updateField('client_type', type.id)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    updateField('client_type', type.id);
                  }}
                  role="button"
                  tabIndex={0}
                  data-testid={`client-type-${type.id}`}
                >
                  <RadioGroupItem value={type.id} id={`ct-${type.id}`} className="border-[#1e3a5f] pointer-events-none flex-shrink-0" />
                  <Label htmlFor={`ct-${type.id}`} className="font-medium text-slate-700 cursor-pointer pointer-events-none">
                    {type.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            
            <div className="space-y-3">
              <Label className="text-slate-700 font-medium">Delivery Authority</Label>
              <RadioGroup
                value={formData.delivery_authority}
                onValueChange={(value) => updateField('delivery_authority', value)}
                className="space-y-2"
              >
                {DELIVERY_AUTHORITY.map((auth) => (
                  <div
                    key={auth.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer select-none touch-manipulation ${
                      formData.delivery_authority === auth.id 
                        ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                        : 'border-slate-200 active:bg-slate-50'
                    }`}
                    onClick={() => updateField('delivery_authority', auth.id)}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      updateField('delivery_authority', auth.id);
                    }}
                    role="button"
                    tabIndex={0}
                    data-testid={`authority-${auth.id}`}
                  >
                    <RadioGroupItem value={auth.id} id={`auth-${auth.id}`} className="border-[#1e3a5f] pointer-events-none flex-shrink-0" />
                    <div className="pointer-events-none">
                      <Label htmlFor={`auth-${auth.id}`} className="font-medium text-slate-700 cursor-pointer">
                        {auth.label}
                      </Label>
                      <p className="text-xs text-slate-500">{auth.description}</p>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        );
        
      case 'project_details':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="project-details-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Project Details
              </h2>
              <p className="text-slate-500 text-lg">
                Tell us about the project
              </p>
            </div>
            
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Project Name *</Label>
                <Input
                  value={formData.project_name}
                  onChange={(e) => updateField('project_name', e.target.value)}
                  placeholder="e.g., 10 Fleet Street CAT B Fit-out"
                  className="h-12 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="project-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  placeholder="e.g., City of London, EC4"
                  className="h-12 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="project-location-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Brief description of the project scope..."
                  className="min-h-[100px] rounded-xl bg-slate-50 border-slate-200"
                  data-testid="project-description-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">MEP Disciplines Required</Label>
                <div className="grid grid-cols-2 gap-3">
                  {MEP_DISCIPLINES.map((disc) => (
                    <div 
                      key={disc.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200"
                    >
                      <Checkbox
                        id={disc.id}
                        checked={formData.mep_disciplines.includes(disc.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            updateField('mep_disciplines', [...formData.mep_disciplines, disc.id]);
                          } else {
                            updateField('mep_disciplines', formData.mep_disciplines.filter(d => d !== disc.id));
                          }
                        }}
                        data-testid={`discipline-${disc.id}`}
                      />
                      <Label htmlFor={disc.id} className="text-slate-700 cursor-pointer">
                        {disc.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'clarity':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="clarity-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Project Clarity
              </h2>
              <p className="text-slate-500 text-lg">
                We need stage, programme, scope, and decision maker to proceed
              </p>
            </div>
            
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Project Stage *</Label>
                <RadioGroup
                  value={formData.project_stage}
                  onValueChange={(value) => updateField('project_stage', value)}
                  className="grid grid-cols-2 gap-2"
                >
                  {PROJECT_STAGES.map((stage) => (
                    <div
                      key={stage.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                        formData.project_stage === stage.id 
                          ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                          : 'border-slate-200'
                      }`}
                      onClick={() => updateField('project_stage', stage.id)}
                      data-testid={`stage-${stage.id}`}
                    >
                      <RadioGroupItem value={stage.id} id={`stage-${stage.id}`} className="border-[#1e3a5f]" />
                      <Label htmlFor={`stage-${stage.id}`} className="text-slate-700 cursor-pointer">
                        {stage.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <Checkbox
                    id="programme"
                    checked={formData.has_programme_summary}
                    onCheckedChange={(checked) => updateField('has_programme_summary', checked)}
                    data-testid="has-programme-checkbox"
                  />
                  <Label htmlFor="programme" className="text-slate-700 cursor-pointer">
                    Programme summary available
                  </Label>
                </div>
                
                <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <Checkbox
                    id="scope"
                    checked={formData.has_scope_summary}
                    onCheckedChange={(checked) => updateField('has_scope_summary', checked)}
                    data-testid="has-scope-checkbox"
                  />
                  <Label htmlFor="scope" className="text-slate-700 cursor-pointer">
                    Scope summary available
                  </Label>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Decision Maker</Label>
                <Input
                  value={formData.decision_maker_name}
                  onChange={(e) => updateField('decision_maker_name', e.target.value)}
                  placeholder="Name of key decision maker"
                  className="h-12 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="decision-maker-input"
                />
              </div>
            </div>
          </div>
        );
        
      case 'value':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="value-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Value & Scale
              </h2>
              <p className="text-slate-500 text-lg">
                Project must meet minimum value thresholds
              </p>
            </div>
            
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Estimated Construction Value (£) *</Label>
                <div className="relative">
                  <PoundSterling className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="number"
                    value={formData.estimated_value}
                    onChange={(e) => updateField('estimated_value', e.target.value)}
                    placeholder="500000"
                    className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="estimated-value-input"
                  />
                </div>
                <p className="text-sm text-slate-500">Minimum: £100,000 (Commercial) / £500,000 (Residential)</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Approximate Area (sq ft)</Label>
                <Input
                  type="number"
                  value={formData.sqft}
                  onChange={(e) => updateField('sqft', e.target.value)}
                  placeholder="10000"
                  className="h-12 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="sqft-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Target Start</Label>
                  <Input
                    type="date"
                    value={formData.timeline_start}
                    onChange={(e) => updateField('timeline_start', e.target.value)}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="timeline-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Target End</Label>
                  <Input
                    type="date"
                    value={formData.timeline_end}
                    onChange={(e) => updateField('timeline_end', e.target.value)}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="timeline-end-input"
                  />
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'review':
        return (
          <div className="space-y-8 animate-fade-in" data-testid="review-step">
            <div className="space-y-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Review Enquiry
              </h2>
              <p className="text-slate-500 text-lg">
                Confirm details before running decision engine
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-5 py-3 border-b border-slate-200">
                  <h3 className="font-medium text-slate-700">Project</h3>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Name</span>
                    <span className="font-medium text-slate-700">{formData.project_name || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sector</span>
                    <span className="chip chip-navy capitalize">
                      {formData.sector === 'other' && formData.sector_custom_name 
                        ? `Other (${formData.sector_custom_name})`
                        : formData.sector?.replace('_', ' ') || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Type</span>
                    <span className="font-medium text-slate-700 uppercase">{formData.project_type?.replace('_', ' ') || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Value</span>
                    <span className="font-medium text-slate-700">
                      {formData.estimated_value ? `£${Number(formData.estimated_value).toLocaleString('en-GB')}` : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-5 py-3 border-b border-slate-200">
                  <h3 className="font-medium text-slate-700">Client</h3>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Contact</span>
                    <span className="font-medium text-slate-700">{formData.client_name || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Company</span>
                    <span className="font-medium text-slate-700">{formData.client_company || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Type</span>
                    <span className="font-medium text-slate-700 capitalize">{formData.client_type?.replace('_', ' ') || '-'}</span>
                  </div>
                </div>
              </div>
              
              {isResidential && (
                <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Home className="w-5 h-5 text-amber-600" />
                    <h3 className="font-medium text-amber-800">Residential Selected Project</h3>
                  </div>
                  <p className="text-sm text-amber-700">
                    This enquiry will be evaluated against residential selection criteria
                  </p>
                </div>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="enquiry-wizard">
      {/* Left - Progress Sidebar */}
      <div className="bg-[#1e3a5f] text-white p-6 md:p-10 md:w-80 lg:w-96">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </div>
          <span className="font-heading text-xl font-bold">New Enquiry</span>
        </div>
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-white/70">Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Steps */}
        <div className="space-y-2">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isCurrent = isResidential && showResidentialWizard 
              ? idx === residentialStep 
              : idx === currentStep;
            const isPast = isResidential && showResidentialWizard 
              ? idx < residentialStep 
              : idx < currentStep;
            
            return (
              <div 
                key={step.id}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                  isCurrent ? 'bg-white/20' : isPast ? 'opacity-70' : 'opacity-40'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isPast ? 'bg-emerald-500' : isCurrent ? 'bg-white/30' : 'bg-white/10'
                }`}>
                  {isPast ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                <span className="text-sm font-medium">{step.title}</span>
              </div>
            );
          })}
        </div>
        
        {isResidential && showResidentialWizard && (
          <div className="mt-6 p-4 bg-amber-500/20 rounded-xl">
            <p className="text-sm text-amber-100">
              Residential Sub-Wizard
            </p>
          </div>
        )}
      </div>
      
      {/* Right - Form Content */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-6 md:p-10 lg:p-16 overflow-auto">
          <div className="max-w-2xl">
            {renderStepContent()}
          </div>
        </div>
        
        {/* Footer Navigation */}
        <div className="border-t border-slate-200 bg-white px-6 md:px-10 lg:px-16 py-4">
          <div className="max-w-2xl flex justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0 && !showResidentialWizard}
              className="rounded-full px-6"
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            {(currentStep === WIZARD_STEPS.length - 1 && !showResidentialWizard) ? (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-8"
                data-testid="wizard-submit-btn"
              >
                {submitting ? 'Creating...' : 'Create & Run Decision'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-8"
                data-testid="wizard-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
